import { pick } from 'lodash';
import {
  hasRecordReference,
  isSimpleGroup,
  isSimpleSelection,
} from '../objects/guards';
import trackEvent from '../utils/analytics';
import { makeScriptRequest } from '../utils/requests';
import {
  BaseTool,
  type IToolActionInput,
  type IToolAvailableInput,
  type IToolEnabledInput,
} from './Tool';
import { type RecordId } from '../types';

interface RecordsForEdit {
  recordType: string;
  recordIds: RecordId[];
}

// map record types which can be bulk-edited to their backend name
const recordTypeMap = {
  features: 'feature',
  releases: 'release',
  initiatives: 'initiative',
  ideas: 'idea',
  epics: 'epic',
  tasks: 'task',
  goals: 'strategic_imperative',
  requirements: 'requirement',
};

/** return record type and ids if the collection can be bulk-edited */
const getRecordsForBulkEdit = (
  selection: fabric.Object
): RecordsForEdit | undefined => {
  if (!isSimpleGroup(selection) && !isSimpleSelection(selection)) {
    return;
  }

  const objects = selection.getObjects();

  if (objects.length <= 1) {
    return;
  }

  const records = objects
    .filter(hasRecordReference)
    .map(shape => pick(shape, 'recordType', 'recordId'));

  // check all objects are records of the same bulk-editable type,
  if (
    records.length < objects.length ||
    [...new Set(records.map(r => r.recordType))].length > 1 ||
    !recordTypeMap[records[0].recordType]
  ) {
    return;
  }

  return {
    recordType: recordTypeMap[records[0].recordType],
    recordIds: records.map(record => record.recordId),
  };
};

class ToolBulkEdit extends BaseTool {
  static toolName = 'Bulk edit';

  static async action({ selection }: IToolActionInput) {
    const records = getRecordsForBulkEdit(selection) as RecordsForEdit;
    const ids = records.recordIds.join(',');
    const url = `/bulk_edits/new?type=${records.recordType}&bulk_edit[configuration_item_ids]=${ids}&bulk_edit[configuration_all_items]=false&bulk_edit[configuration_action_origin]=whiteboards`;

    await makeScriptRequest(url);

    const form = document.querySelector('#modal-window #new_bulk_edit');
    if (form) {
      const track = () => {
        trackEvent(
          'Feature-7270954775694955442',
          'User applies whiteboard bulk edits'
        );
        form.removeEventListener('submit', track);
      };
      form.addEventListener('submit', track);
    }

    trackEvent(
      'Feature-7270954775694955442',
      'User opens whiteboard bulk edits'
    );
  }

  static enabled({ selection }: IToolEnabledInput) {
    return !!(selection && getRecordsForBulkEdit(selection));
  }

  static available({ selection }: IToolAvailableInput) {
    return !!(selection && getRecordsForBulkEdit(selection));
  }
}

export default ToolBulkEdit;
